import { useCallback, useMemo } from 'react';
import { api } from '../../lib/api/client.js';
import type { AdminLesson, AdminUser, AdminUserSummary } from '../../lib/api/types.js';
import { Button } from '../../ui/primitives/Button.tsx';
import { Surface } from '../../ui/primitives/Surface.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import { LoadingState } from '../../ui/components/LoadingState/LoadingState.tsx';
import { useAsyncData } from '../../ui/components/LoadingState/useAsyncData.ts';
import { StatRow, StatTile } from '../../ui/components/StatTile/StatTile.tsx';
import { labelForType } from './ExerciseConfigForm.tsx';
import styles from './admin.module.css';
import overviewStyles from './AdminOverview.module.css';

/**
 * The administrator's landing page — `/admin` itself, per the design canvas'
 * "Visão geral" turn.
 *
 * EVERY FIGURE IS A REAL COUNT, the same rule `StatTile` and `UsersList`
 * already hold the rest of the admin surface to: nothing here is estimated,
 * and a count the platform does not have is not shown rather than invented.
 * That is also why "Novas contas" reads 30 dias rather than the canvas
 * mock's 7 — `AdminUserSummary.recent` is the field the server actually
 * returns, and the copy says what it counts.
 */
/** `01/09/2026` — a date read down a column, same shape `UsersList` uses. */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

interface Overview {
  lessons: AdminLesson[];
  summary: AdminUserSummary;
  recentUsers: AdminUser[];
}

export function AdminOverview() {
  const loadOverview = useCallback(async (): Promise<Overview | null> => {
    const [lessonsResponse, usersResponse] = await Promise.all([
      api.adminLessons(),
      api.adminUsers({ pageSize: 5, sort: 'createdAt', direction: 'desc' }),
    ]);
    return {
      lessons: lessonsResponse.lessons,
      summary: usersResponse.summary,
      recentUsers: usersResponse.users,
    };
  }, []);

  const { status, data, showLoading, reload } = useAsyncData(loadOverview, null);

  const exerciseCount = useMemo(
    () => data?.lessons.reduce((sum, lesson) => sum + lesson.exercises.length, 0) ?? 0,
    [data],
  );

  /** Exercise types in use, with a real count each — the "por tipo" panel. */
  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const lesson of data?.lessons ?? []) {
      for (const exercise of lesson.exercises) {
        counts.set(exercise.exerciseType, (counts.get(exercise.exerciseType) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([type, count]) => ({ type, count, label: labelForType(type) }))
      .sort((a, b) => b.count - a.count);
  }, [data]);

  const maxTypeCount = typeCounts.reduce((max, entry) => Math.max(max, entry.count), 0);

  if (status === 'loading') return showLoading ? <LoadingState label="Carregando a visão geral" /> : null;

  if (status === 'unavailable' || !data) {
    return (
      <div className={styles.page}>
        <Text as="h1" variant="subheading">
          Não foi possível carregar a visão geral
        </Text>
        <span>
          <Button onClick={() => reload({ force: true })}>Tentar novamente</Button>
        </span>
      </div>
    );
  }

  const { lessons, summary, recentUsers } = data;

  return (
    <div className={styles.page} data-testid="admin-overview">
      <header className={styles.header}>
        <div className={styles.heading}>
          <Text as="h1" variant="subheading">
            Visão geral
          </Text>
          <Text as="p" variant="body-sm" tone="muted">
            O estado atual da plataforma, em números.
          </Text>
        </div>
      </header>

      <StatRow label="Resumo da plataforma">
        <StatTile label="Lições" value={lessons.length} hint={`${exerciseCount} exercícios no total`} />
        <StatTile
          label="Tipos em uso"
          value={typeCounts.length}
          hint={typeCounts.map((entry) => entry.label).join(', ') || 'Nenhum ainda'}
        />
        <StatTile
          label="Usuários"
          value={summary.total}
          hint={`${summary.admins} administradores · ${summary.learners} alunos`}
          icon="user"
        />
        <StatTile
          label="Novas contas"
          value={summary.recent}
          hint="Criadas nos últimos 30 dias"
        />
      </StatRow>

      <div className={styles.charts}>
        <Surface layer={1} radius="lg" inset>
          <Text as="h2" variant="subheading">
            Exercícios por tipo
          </Text>
          {typeCounts.length === 0 ? (
            <Text as="p" variant="body-sm" tone="muted">
              Nenhum exercício ainda.
            </Text>
          ) : (
            <ul className={overviewStyles.typeList}>
              {typeCounts.map((entry) => (
                <li key={entry.type} className={overviewStyles.typeRow}>
                  <div className={overviewStyles.typeHead}>
                    <Text as="span" variant="body-sm">
                      {entry.label}
                    </Text>
                    <span className="type-numeric-inline">{entry.count}</span>
                  </div>
                  <div className={overviewStyles.typeTrack}>
                    <span
                      className={overviewStyles.typeFill}
                      style={{ width: `${(entry.count / maxTypeCount) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Surface>

        <Surface layer={1} radius="lg" inset>
          <Text as="h2" variant="subheading">
            Usuários recentes
          </Text>
          {recentUsers.length === 0 ? (
            <Text as="p" variant="body-sm" tone="muted">
              Nenhum usuário ainda.
            </Text>
          ) : (
            <ul className={overviewStyles.userList}>
              {recentUsers.map((account) => (
                <li key={account.id} className={overviewStyles.userRow}>
                  <Text as="span" variant="body-sm">
                    {account.name}
                  </Text>
                  <span className="type-numeric-inline">{formatDate(account.createdAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </Surface>
      </div>
    </div>
  );
}
